/**
 * Multiplayer presence (TASK-926) — renders other signed-in players in real
 * time.
 *
 * While the local player is in the world (isPlaying), the system publishes
 * pose updates and consumes an authoritative roster through Arena's
 * same-origin world WebSocket. Each remote player is rendered as a
 * Quaternius character (variant picked deterministically from the anonymized
 * player id), position/yaw smoothly lerped between roster updates, Walk clip
 * while moving / Idle while still, with a floating chip-style DOM name label.
 * The local player is never rendered (FPV) but still broadcasts.
 *
 * Portal never receives or serves presence coordinates.
 */
import * as pc from 'playcanvas'
import { useArenaStore } from '../../store'
import type { WorldMember } from '../../../shared/worldProtocol'
import type { WorldPresenceTransport } from '../../world/worldSocketClient'
import { HUMAN_CLIP_MAP, pickVariant, playClip, spawnCharacter } from '../characters'
import type { CharacterHandle } from '../characters'
import { computeLabelScreenPos } from './labels'
import { injectWorldCardStyles } from './labelStyles'
import { createWorldOverlay } from './interiorShared'

export const HEARTBEAT_MS = 250 // POST own position 4x/s

/** Exponential smoothing rates (per second) for remote pose interpolation. */
export const POS_LERP_RATE = 10
export const YAW_LERP_RATE = 12

/** Farther than this from the lerp target = the player is walking. */
export const MOVE_EPS = 0.05

const WALK_CLIP = 'CharacterArmature|Walk'
const IDLE_CLIP = HUMAN_CLIP_MAP.idle
const CHIP_HEIGHT_OFFSET = 2.15 // world-units above the avatar's feet
const RAD_TO_DEG = 180 / Math.PI

// ── Pure helpers (exported for tests) ──────────────────────────────────────

/** Frame-rate-independent smoothing factor for exponential lerp. */
export function smoothFactor(rate: number, dt: number): number {
  return 1 - Math.exp(-rate * dt)
}

/** Lerp between two angles (radians) along the shortest arc. */
export function lerpAngle(a: number, b: number, t: number): number {
  const TWO_PI = Math.PI * 2
  let delta = (b - a) % TWO_PI
  if (delta > Math.PI) delta -= TWO_PI
  if (delta < -Math.PI) delta += TWO_PI
  return a + delta * t
}

/** Diff the rendered id set against an incoming roster id list. */
export function diffRoster(
  existing: Iterable<string>,
  incoming: Iterable<string>,
): { toSpawn: string[]; toDespawn: string[] } {
  const have = new Set(existing)
  const want = new Set(incoming)
  const toSpawn: string[] = []
  const toDespawn: string[] = []
  for (const id of want) if (!have.has(id)) toSpawn.push(id)
  for (const id of have) if (!want.has(id)) toDespawn.push(id)
  return { toSpawn, toDespawn }
}

// ── Renderer seam ──────────────────────────────────────────────────────────

export interface RemotePose {
  x: number
  z: number
  yaw: number
}

/**
 * What the PresenceSystem drives. The production implementation
 * (createPcAvatarRenderer) spawns PlayCanvas characters + DOM chips; tests
 * inject a stub so no engine/WebGL is needed.
 */
export interface AvatarRenderer {
  spawn(id: string, name: string, pose: RemotePose): void
  move(id: string, pose: RemotePose, moving: boolean): void
  despawn(id: string): void
  destroy(): void
}

/**
 * Production renderer: one Quaternius character per remote player (variant
 * deterministic from the anonymized player id) plus a floating chip-style DOM
 * name label projected above the avatar's head each frame.
 */
export function createPcAvatarRenderer(
  app: pc.Application,
  cameraEntity: pc.Entity,
): AvatarRenderer {
  injectWorldCardStyles()

  const overlay = createWorldOverlay(app)
  const container = overlay.element

  const chars = new Map<string, CharacterHandle>()
  const chips = new Map<string, HTMLDivElement>()
  const tmpVec = new pc.Vec3()

  function makeChip(name: string): HTMLDivElement {
    const div = document.createElement('div')
    Object.assign(div.style, {
      position: 'absolute',
      fontFamily: 'ui-monospace, monospace',
      fontSize: '12px',
      lineHeight: '1.4',
      whiteSpace: 'nowrap',
      userSelect: 'none',
      display: 'none',
    })
    const title = document.createElement('span')
    title.className = 'wchip-title'
    title.textContent = name
    const chip = document.createElement('div')
    chip.className = 'wchip'
    const dot = document.createElement('span')
    dot.className = 'wchip-dot'
    dot.style.background = '#34d399'
    chip.appendChild(dot)
    chip.appendChild(title)
    div.appendChild(chip)
    container.appendChild(div)
    return div
  }

  return {
    spawn(id, name, pose) {
      const handle = spawnCharacter(app, pickVariant(id), [pose.x, 0, pose.z])
      // Quaternius characters face +Z at identity; yaw 0 means facing -Z
      // (the camera's forward convention in player.ts), hence the 180 offset.
      handle.entity.setEulerAngles(0, 180 - pose.yaw * RAD_TO_DEG, 0)
      chars.set(id, handle)
      chips.set(id, makeChip(name))
    },

    move(id, pose, moving) {
      const handle = chars.get(id)
      if (!handle) return
      handle.entity.setPosition(pose.x, 0, pose.z)
      handle.entity.setEulerAngles(0, 180 - pose.yaw * RAD_TO_DEG, 0)
      playClip(handle.entity, moving ? WALK_CLIP : IDLE_CLIP)

      const chip = chips.get(id)
      if (!chip) return
      const camComp = cameraEntity.camera
      if (!camComp) return
      const screenPos = computeLabelScreenPos(
        pose.x,
        CHIP_HEIGHT_OFFSET,
        pose.z,
        (wv, out) => camComp.worldToScreen(wv, out),
        tmpVec,
      )
      if (!screenPos) {
        chip.style.display = 'none'
        return
      }
      chip.style.display = 'block'
      chip.style.left = `${screenPos.x}px`
      chip.style.top = `${screenPos.y}px`
      chip.style.transform = 'translate(-50%, -100%)'
    },

    despawn(id) {
      chars.get(id)?.destroy()
      chars.delete(id)
      chips.get(id)?.remove()
      chips.delete(id)
    },

    destroy() {
      for (const handle of chars.values()) handle.destroy()
      chars.clear()
      chips.clear()
      overlay.dispose()
    },
  }
}

// ── The system ─────────────────────────────────────────────────────────────

interface RemoteTarget {
  name: string
  x: number
  z: number
  yaw: number
}

export interface PresenceSystemDeps {
  renderer: AvatarRenderer
  /** The local player's current world pose (from the FPV controller). */
  getSelf: () => RemotePose
  /** Production transport: authenticated same-origin Arena WebSocket. */
  transport: WorldPresenceTransport
}

export class PresenceSystem {
  private renderer!: AvatarRenderer
  private getSelf!: () => RemotePose
  private transport!: WorldPresenceTransport

  private heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private storeUnsub: (() => void) | null = null
  private connectionGeneration = 0

  private readonly targets = new Map<string, RemoteTarget>()
  private readonly current = new Map<string, RemotePose>()
  /** Serialize polls — a slow response must not interleave with the next. */

  init(deps: PresenceSystemDeps): void {
    this.renderer = deps.renderer
    this.getSelf = deps.getSelf
    this.transport = deps.transport

    this.storeUnsub = useArenaStore.subscribe((state, prev) => {
      if (state.apiGeneration !== prev.apiGeneration) {
        this.stopTimers()
        this.clearRemotes()
        if (state.apiClient !== null) this.syncTimers()
        return
      }
      this.syncTimers()
    })
    this.syncTimers()
  }

  /** True while the loops should run: playing + a presence-capable client. */
  private shouldRun(): boolean {
    return useArenaStore.getState().isPlaying
  }

  private syncTimers(): void {
    const run = this.shouldRun()
    if (run && this.heartbeatTimer === null) {
      const connectionGeneration = ++this.connectionGeneration
      const apiGeneration = useArenaStore.getState().apiGeneration
      // Claim the lifecycle before publishing state. Store subscribers run
      // synchronously, so this prevents a `connecting` update from starting a
      // duplicate transport and heartbeat loop.
      this.heartbeatTimer = setInterval(() => void this.heartbeat(), HEARTBEAT_MS)
      useArenaStore.getState().setPresenceStatus('connecting')
      this.transport.connect(
        (members) => {
          if (!this.ownsConnection(connectionGeneration, apiGeneration)) return
          this.applyWorldRoster(members)
        },
        (status) => {
          if (!this.ownsConnection(connectionGeneration, apiGeneration)) return
          useArenaStore.getState().setPresenceStatus(status)
        },
      )
    } else if (!run && this.heartbeatTimer !== null) {
      this.stopTimers()
    }
  }

  private stopTimers(): void {
    this.connectionGeneration += 1
    if (this.heartbeatTimer !== null) clearInterval(this.heartbeatTimer)
    this.heartbeatTimer = null
    this.transport.disconnect()
    useArenaStore.getState().setPresenceStatus('disconnected')
    this.clearRemotes()
  }

  private ownsConnection(connectionGeneration: number, apiGeneration: number): boolean {
    const state = useArenaStore.getState()
    return connectionGeneration === this.connectionGeneration &&
      apiGeneration === state.apiGeneration
  }

  private heartbeat(): void {
    if (!this.shouldRun()) return
    this.transport.updatePose(this.getSelf())
  }

  /** Spawn new players, despawn absent ones, refresh lerp targets. */
  applyRoster(players: Array<{
    id: string
    name: string
    x: number
    z: number
    yaw: number
  }>): void {
    const { toSpawn, toDespawn } = diffRoster(
      this.targets.keys(),
      players.map((p) => p.id),
    )
    for (const id of toDespawn) {
      this.targets.delete(id)
      this.current.delete(id)
      this.renderer.despawn(id)
    }
    for (const p of players) {
      this.targets.set(p.id, { name: p.name, x: p.x, z: p.z, yaw: p.yaw })
      if (toSpawn.includes(p.id)) {
        // Spawn AT the first reported pose — no lerp-in from origin.
        this.current.set(p.id, { x: p.x, z: p.z, yaw: p.yaw })
        this.renderer.spawn(p.id, p.name, { x: p.x, z: p.z, yaw: p.yaw })
      }
    }
  }

  private applyWorldRoster(members: WorldMember[]): void {
    useArenaStore.getState().setActiveWorldMembers(members)
    this.applyRoster(members.map((member) => ({
      id: member.playerId,
      name: member.displayName,
      x: member.pose.x,
      z: member.pose.z,
      yaw: member.pose.yaw,
      ageMs: Math.max(0, Date.now() - Date.parse(member.lastSeenAt)),
    })))
  }

  setRoom(roomId: string): void {
    this.transport.changeRoom(roomId)
    useArenaStore.getState().setCurrentWorldRoomId(roomId)
  }

  /** Per-frame: exponentially smooth each remote pose toward its target. */
  frame(dt: number): void {
    if (this.targets.size === 0) return
    const posT = smoothFactor(POS_LERP_RATE, dt)
    const yawT = smoothFactor(YAW_LERP_RATE, dt)
    for (const [id, target] of this.targets) {
      const cur = this.current.get(id)
      if (!cur) continue
      const dx = target.x - cur.x
      const dz = target.z - cur.z
      const moving = Math.sqrt(dx * dx + dz * dz) > MOVE_EPS
      cur.x += dx * posT
      cur.z += dz * posT
      cur.yaw = lerpAngle(cur.yaw, target.yaw, yawT)
      this.renderer.move(id, cur, moving)
    }
  }

  private clearRemotes(): void {
    for (const id of this.targets.keys()) this.renderer.despawn(id)
    this.targets.clear()
    this.current.clear()
    useArenaStore.getState().setActiveWorldMembers([])
  }

  destroy(): void {
    this.storeUnsub?.()
    this.storeUnsub = null
    this.stopTimers()
    this.renderer?.destroy()
  }
}
